import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  rehabPlans: [],
  currentRehabPlan: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Rehab Plans (optionally by patient ID)
export const fetchRehabPlans = createAsyncThunk(
  'rehabPlans/fetchRehabPlans',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/rehab-plans/patient/${patientId}`
        : '/rehab-plans';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch rehab plans'
      );
    }
  }
);

// Fetch a single Rehab Plan by ID
export const fetchRehabPlan = createAsyncThunk(
  'rehabPlans/fetchRehabPlan',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/rehab-plans/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch rehab plan'
      );
    }
  }
);

// Create a new Rehab Plan
export const createRehabPlan = createAsyncThunk(
  'rehabPlans/createRehabPlan',
  async (rehabPlanData, { rejectWithValue }) => {
    try {
      const response = await api.post('/rehab-plans', rehabPlanData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create rehab plan'
      );
    }
  }
);

// Update a Rehab Plan
export const updateRehabPlan = createAsyncThunk(
  'rehabPlans/updateRehabPlan',
  async ({ case_id, rehabPlanData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/rehab-plans/case/${case_id}`, rehabPlanData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update rehab plan'
      );
    }
  }
);

// Delete a Rehab Plan
export const deleteRehabPlan = createAsyncThunk(
  'rehabPlans/deleteRehabPlan',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/rehab-plans/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete rehab plan'
      );
    }
  }
);

// ==================== Slice ====================
const individualisedRehabPlanSlice = createSlice({
  name: 'rehabPlans',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRehabPlan: (state, action) => {
      state.currentRehabPlan = action.payload;
    },
    clearCurrentRehabPlan: (state) => {
      state.currentRehabPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchRehabPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRehabPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rehabPlans = action.payload;
      })
      .addCase(fetchRehabPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchRehabPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRehabPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentRehabPlan = action.payload;
      })
      .addCase(fetchRehabPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createRehabPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRehabPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rehabPlans.unshift(action.payload);
      })
      .addCase(createRehabPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateRehabPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRehabPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.rehabPlans.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.rehabPlans[index] = updated;
        }

        if (state.currentRehabPlan?.id === updated.id) {
          state.currentRehabPlan = updated;
        }
      })
      .addCase(updateRehabPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteRehabPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRehabPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rehabPlans = state.rehabPlans.filter((d) => d.id !== action.payload);

        if (state.currentRehabPlan?.id === action.payload) {
          state.currentRehabPlan = null;
        }
      })
      .addCase(deleteRehabPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearError, 
  setCurrentRehabPlan, 
  clearCurrentRehabPlan 
} = individualisedRehabPlanSlice.actions;

export default individualisedRehabPlanSlice.reducer;